import React, { useState, useEffect } from "react";
import api from "../lib/api";
import { useAuthStore } from "../store/authStore";



/* ─── types ──────────────────────────────────────────────── */
type Frequency = "weekly" | "bi_weekly" | "monthly";
type PlanStatus = "active" | "paused" | "cancelled";

interface Plan {
  id: number;
  meter_id: number;
  meter_serial: string;
  amount: number;
  frequency: Frequency;
  next_run: string;
  last_run: string | null;
  status: PlanStatus;
  phone_number: string;
  run_count: number;
  total_spent: number;
}

interface Meter {
  id: number;
  serial_number: string;
  property?: { token_value: number; unit_label: string };
}

/* ─── helpers ────────────────────────────────────────────── */
const FREQ_LABELS: Record<Frequency, string> = {
  weekly: "Weekly",
  bi_weekly: "Every 2 Weeks",
  monthly: "Monthly",
};

const STATUS_META: Record<PlanStatus, { label: string; color: string; dot: string }> = {
  active: { label: "Active", color: "bg-emerald-50 text-emerald-600", dot: "bg-emerald-400" },
  paused: { label: "Paused", color: "bg-amber-50 text-amber-600", dot: "bg-amber-400" },
  cancelled: { label: "Cancelled", color: "bg-gray-100 text-gray-400", dot: "bg-gray-300" },
};

function Skeleton({ className = "" }: { className?: string }) {
  return <div className={`animate-pulse bg-gray-100 rounded-xl ${className}`} />;
}

function Toggle({ value, onChange }: { value: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      onClick={() => onChange(!value)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${value ? "bg-sky-500" : "bg-gray-200"}`}
    >
      <span className={`inline-block h-4 w-4 rounded-full bg-white shadow-md transform transition-transform ${value ? "translate-x-6" : "translate-x-1"}`} />
    </button>
  );
}

const PRESET_AMOUNTS = [100, 200, 500, 1000, 2000];

/* ─── modal ──────────────────────────────────────────────── */
function PlanModal({
  meter,
  existing,
  onClose,
  onSave,
}: {
  meter: Meter | null;
  existing: Plan | null;
  onClose: () => void;
  onSave: (plan: Partial<Plan>) => void;
}) {
  const { user } = useAuthStore();
  const [amount, setAmount] = useState(existing?.amount ?? 500);
  const [frequency, setFrequency] = useState<Frequency>(existing?.frequency ?? "monthly");
  const [phone, setPhone] = useState(existing?.phone_number ?? user?.phone_number ?? "");
  const [customAmount, setCustomAmount] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const units = meter?.property?.token_value ? amount / meter.property.token_value : 0;

  const nextRunDate = () => {
    const d = new Date();
    if (frequency === "weekly") d.setDate(d.getDate() + 7);
    else if (frequency === "bi_weekly") d.setDate(d.getDate() + 14);
    else d.setMonth(d.getMonth() + 1);
    return d.toLocaleDateString("en-KE", { dateStyle: "medium" });
  };

  const annualCost = () => {
    if (frequency === "weekly") return amount * 52;
    if (frequency === "bi_weekly") return amount * 26;
    return amount * 12;
  };

  const handleSave = () => {
    if (!phone.match(/^(\+254|07|01)\d{8,9}$/)) {
      setError("Enter a valid Kenyan phone number (e.g. 0712345678)");
      return;
    }
    setSaving(true);
    setError("");
    onSave({ meter_id: meter?.id, amount, frequency, phone_number: phone });
    setSaving(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden">
        <div className="bg-gradient-to-r from-[#0f2557] to-[#0e2050] px-6 py-5">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-white font-bold text-[15px]">
                {existing ? "Edit Recurring Plan" : "New Recurring Plan"}
              </h2>
              {meter && <p className="text-sky-300/70 text-[12px] mt-0.5">Meter · {meter.serial_number}</p>}
            </div>
            <button onClick={onClose} className="text-white/50 hover:text-white transition-colors">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-5 h-5">
                <path d="M18 6 6 18M6 6l12 12" strokeLinecap="round" />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-6 space-y-5">

          {/* frequency */}
          <div>
            <label className="block text-[12px] font-semibold text-gray-600 uppercase tracking-widest mb-2">
              Top-Up Frequency
            </label>
            <div className="grid grid-cols-3 gap-2">
              {(["weekly", "bi_weekly", "monthly"] as Frequency[]).map((f) => (
                <button
                  key={f}
                  onClick={() => setFrequency(f)}
                  className={`py-3 rounded-xl border text-[12.5px] font-semibold transition-all ${
                    frequency === f
                      ? "bg-sky-500 text-white border-sky-500 shadow-sm"
                      : "bg-white text-gray-600 border-gray-200 hover:border-sky-300"
                  }`}
                >
                  {FREQ_LABELS[f]}
                </button>
              ))}
            </div>
          </div>

          {/* amount */}
          <div>
            <label className="block text-[12px] font-semibold text-gray-600 uppercase tracking-widest mb-2">
              Amount per Cycle (KES)
            </label>
            <div className="flex flex-wrap gap-2 mb-2">
              {PRESET_AMOUNTS.map((a) => (
                <button
                  key={a}
                  onClick={() => { setAmount(a); setCustomAmount(false); }}
                  className={`px-3 py-1.5 rounded-lg text-[12.5px] font-semibold border transition-all ${
                    amount === a && !customAmount
                      ? "bg-sky-500 text-white border-sky-500 shadow-sm"
                      : "bg-white text-gray-600 border-gray-200 hover:border-sky-300"
                  }`}
                >
                  KES {a.toLocaleString()}
                </button>
              ))}
              <button
                onClick={() => setCustomAmount(true)}
                className={`px-3 py-1.5 rounded-lg text-[12.5px] font-semibold border transition-all ${
                  customAmount ? "bg-sky-500 text-white border-sky-500" : "bg-white text-gray-600 border-gray-200 hover:border-sky-300"
                }`}
              >
                Custom
              </button>
            </div>
            {customAmount && (
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(Number(e.target.value))}
                placeholder="Enter amount"
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-sky-300"
              />
            )}
          </div>

          {/* phone */}
          <div>
            <label className="block text-[12px] font-semibold text-gray-600 uppercase tracking-widest mb-2">
              M-Pesa Phone Number
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[12px] font-semibold text-gray-400">🇰🇪</span>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="0712 345 678"
                className="w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-xl text-[13px] focus:outline-none focus:ring-2 focus:ring-sky-300"
              />
            </div>
          </div>

          {error && (
            <p className="text-[12.5px] text-red-500 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</p>
          )}

          {/* summary card */}
          <div className="bg-gradient-to-br from-sky-50 to-blue-50 border border-sky-100 rounded-xl p-4 space-y-2">
            <p className="text-[12px] font-semibold text-sky-700 uppercase tracking-widest">Plan Summary</p>
            <div className="grid grid-cols-2 gap-y-1.5 text-[12.5px]">
              <span className="text-gray-500">Frequency</span>
              <span className="font-semibold text-gray-800">{FREQ_LABELS[frequency]}</span>
              <span className="text-gray-500">Amount / cycle</span>
              <span className="font-semibold text-gray-800">KES {amount.toLocaleString()}</span>
              {units > 0 && (
                <>
                  <span className="text-gray-500">Units / cycle</span>
                  <span className="font-semibold text-sky-600">{units.toFixed(1)} {meter?.property?.unit_label}</span>
                </>
              )}
              <span className="text-gray-500">Next run</span>
              <span className="font-semibold text-gray-800">{nextRunDate()}</span>
              <span className="text-gray-500">Est. annual cost</span>
              <span className="font-semibold text-emerald-600">KES {annualCost().toLocaleString()}</span>
            </div>
          </div>

          <div className="flex gap-3 pt-1">
            <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-[13px] font-semibold text-gray-600 hover:bg-gray-50 transition-all">
              Cancel
            </button>
            <button onClick={handleSave} disabled={saving} className="flex-1 py-2.5 rounded-xl bg-sky-500 text-white text-[13px] font-semibold hover:bg-sky-600 transition-all shadow-sm disabled:opacity-60">
              {saving ? "Saving…" : existing ? "Update Plan" : "Create Plan"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── plan card ─────────────────────────────────────────── */
function PlanCard({
  plan,
  onToggle,
  onEdit,
  onCancel,
}: {
  plan: Plan;
  onToggle: (id: number, paused: boolean) => void;
  onEdit: (plan: Plan) => void;
  onCancel: (id: number) => void;
}) {
  const meta = STATUS_META[plan.status];
  const isActive = plan.status === "active";
  const isPaused = plan.status === "paused";

  return (
    <div className={`bg-white rounded-2xl border shadow-sm p-5 transition-all ${isActive ? "border-sky-100" : "border-gray-100 opacity-80"}`}>
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${isActive ? "bg-emerald-50" : "bg-gray-100"}`}>
            <svg viewBox="0 0 24 24" fill="none" stroke={isActive ? "#10b981" : "#9ca3af"} strokeWidth={1.8} className="w-5 h-5">
              <path d="M12 8v4l3 3" strokeLinecap="round"/>
              <circle cx="12" cy="12" r="9"/>
            </svg>
          </div>
          <div>
            <p className="text-[14px] font-bold text-gray-800">Meter · {plan.meter_serial}</p>
            <span className={`inline-flex items-center gap-1.5 mt-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ${meta.color}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
              {meta.label}
            </span>
          </div>
        </div>
        {(isActive || isPaused) && (
          <Toggle
            value={isActive}
            onChange={(v) => onToggle(plan.id, !v)}
          />
        )}
      </div>

      <div className="grid grid-cols-2 gap-2.5 mb-4">
        {[
          { label: "Frequency", value: FREQ_LABELS[plan.frequency] },
          { label: "Amount", value: `KES ${plan.amount.toLocaleString()}` },
          { label: "Next Run", value: new Date(plan.next_run).toLocaleDateString("en-KE", { dateStyle: "medium" }) },
          { label: "Total Spent", value: `KES ${plan.total_spent.toLocaleString()}` },
          { label: "M-Pesa", value: plan.phone_number },
          { label: "Cycles Run", value: plan.run_count.toString() },
        ].map((d) => (
          <div key={d.label} className="bg-gray-50 rounded-xl px-3 py-2">
            <p className="text-[10.5px] text-gray-400 uppercase tracking-wider font-medium">{d.label}</p>
            <p className="text-[12.5px] font-semibold text-gray-800 mt-0.5 truncate">{d.value}</p>
          </div>
        ))}
      </div>

      {plan.status !== "cancelled" && (
        <div className="flex gap-2">
          <button onClick={() => onEdit(plan)} className="flex-1 py-2 rounded-xl border border-gray-200 text-[12.5px] font-semibold text-gray-600 hover:bg-gray-50 transition-all">
            Edit
          </button>
          <button onClick={() => onCancel(plan.id)} className="flex-1 py-2 rounded-xl border border-red-100 text-[12.5px] font-semibold text-red-500 hover:bg-red-50 transition-all">
            Cancel Plan
          </button>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   PAGE
═══════════════════════════════════════════════════════════ */
export default function RecurringBillingPage() {
  const { user } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [meters, setMeters] = useState<Meter[]>([]);
  const [plans, setPlans] = useState<Plan[]>([
    {
      id: 1, meter_id: 1, meter_serial: "MTR-001",
      amount: 500, frequency: "monthly",
      next_run: new Date(Date.now() + 18 * 86400000).toISOString(),
      last_run: new Date(Date.now() - 12 * 86400000).toISOString(),
      status: "active", phone_number: user?.phone_number ?? "0712345678",
      run_count: 7, total_spent: 3500,
    },
  ]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editPlan, setEditPlan] = useState<Plan | null>(null);
  const [modalMeter, setModalMeter] = useState<Meter | null>(null);
  const [filter, setFilter] = useState<"all" | PlanStatus>("all");

  useEffect(() => {
    api.get(`/meters/`)
      .then((r) => setMeters(r.data?.results ?? r.data ?? []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const openCreate = (meter: Meter) => {
    setEditPlan(null);
    setModalMeter(meter);
    setModalOpen(true);
  };

  const openEdit = (plan: Plan) => {
    const m = meters.find((x) => x.id === plan.meter_id) ?? null;
    setEditPlan(plan);
    setModalMeter(m);
    setModalOpen(true);
  };

  const handleSave = (data: Partial<Plan>) => {
    if (editPlan) {
      setPlans((prev) => prev.map((p) => p.id === editPlan.id ? { ...p, ...data } : p));
    } else {
      const now = new Date();
      const nextRun = new Date(now);
      if (data.frequency === "weekly") nextRun.setDate(now.getDate() + 7);
      else if (data.frequency === "bi_weekly") nextRun.setDate(now.getDate() + 14);
      else nextRun.setMonth(now.getMonth() + 1);

      setPlans((prev) => [...prev, {
        id: Date.now(),
        meter_id: data.meter_id!,
        meter_serial: meters.find((m) => m.id === data.meter_id)?.serial_number ?? "—",
        amount: data.amount!,
        frequency: data.frequency!,
        next_run: nextRun.toISOString(),
        last_run: null,
        status: "active",
        phone_number: data.phone_number!,
        run_count: 0,
        total_spent: 0,
      }]);
    }
  };

  const handleToggle = (id: number, paused: boolean) => {
    setPlans((prev) => prev.map((p) => p.id === id ? { ...p, status: paused ? "paused" : "active" } : p));
  };

  const handleCancel = (id: number) => {
    setPlans((prev) => prev.map((p) => p.id === id ? { ...p, status: "cancelled" } : p));
  };

  const filtered = filter === "all" ? plans : plans.filter((p) => p.status === filter);
  const activePlans = plans.filter((p) => p.status === "active");
  const totalAnnual = activePlans.reduce((s, p) => {
    if (p.frequency === "weekly") return s + p.amount * 52;
    if (p.frequency === "bi_weekly") return s + p.amount * 26;
    return s + p.amount * 12;
  }, 0);

  const metersWithoutPlan = meters.filter((m) => !plans.some((p) => p.meter_id === m.id && p.status !== "cancelled"));

  const TABS: { key: "all" | PlanStatus; label: string }[] = [
    { key: "all", label: "All" },
    { key: "active", label: "Active" },
    { key: "paused", label: "Paused" },
    { key: "cancelled", label: "Cancelled" },
  ];

  return (
    <>
      <div className="space-y-7">

        {/* ── header ── */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-[22px] font-bold text-gray-800 leading-tight">Recurring Billing</h1>
            <p className="text-[13px] text-gray-400 mt-0.5">Schedule automatic water token purchases on a regular cycle</p>
          </div>
        </div>

        {/* ── summary strip ── */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: "Active Plans", value: activePlans.length.toString(), color: "bg-sky-50", iconColor: "#0ea5e9" },
            { label: "Paused Plans", value: plans.filter((p) => p.status === "paused").length.toString(), color: "bg-amber-50", iconColor: "#f59e0b" },
            { label: "Total Cycles Run", value: plans.reduce((s, p) => s + p.run_count, 0).toString(), color: "bg-violet-50", iconColor: "#6366f1" },
            { label: "Est. Annual Spend", value: `KES ${totalAnnual.toLocaleString()}`, color: "bg-emerald-50", iconColor: "#10b981" },
          ].map((s) => (
            <div key={s.label} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${s.color}`}>
                <svg viewBox="0 0 24 24" fill="none" stroke={s.iconColor} strokeWidth={1.8} className="w-5 h-5">
                  <path d="M12 8v4l3 3" strokeLinecap="round"/><circle cx="12" cy="12" r="9"/>
                </svg>
              </div>
              <div>
                <p className="text-[10.5px] text-gray-400 uppercase tracking-widest font-medium">{s.label}</p>
                <p className="text-[16px] font-bold text-gray-800 leading-tight">{s.value}</p>
              </div>
            </div>
          ))}
        </div>

        {/* ── filter tabs ── */}
        <div className="flex gap-1 bg-gray-100 rounded-xl p-1 w-fit">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setFilter(t.key)}
              className={`px-3.5 py-2 rounded-lg text-[12.5px] font-semibold transition-all ${
                filter === t.key ? "bg-white text-gray-800 shadow-sm" : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* ── plans grid ── */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1,2].map((i) => <Skeleton key={i} className="h-72" />)}
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-2xl border border-dashed border-gray-200 p-10 text-center">
            <p className="font-semibold text-gray-700 text-[14px]">No plans found</p>
            <p className="text-[12.5px] text-gray-400 mt-1">Create a recurring plan below.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((plan) => (
              <PlanCard
                key={plan.id}
                plan={plan}
                onToggle={handleToggle}
                onEdit={openEdit}
                onCancel={handleCancel}
              />
            ))}
          </div>
        )}

        {/* ── add for new meter ── */}
        {metersWithoutPlan.length > 0 && (
          <div>
            <h2 className="text-[14px] font-bold text-gray-700 mb-3">Add a Plan for a Meter</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {metersWithoutPlan.map((m) => (
                <button
                  key={m.id}
                  onClick={() => openCreate(m)}
                  className="group bg-white rounded-2xl border border-dashed border-gray-200 hover:border-sky-300 hover:bg-sky-50/40 p-5 text-left transition-all"
                >
                  <div className="w-10 h-10 rounded-xl bg-gray-50 group-hover:bg-sky-100 flex items-center justify-center mb-3 transition-all">
                    <svg viewBox="0 0 24 24" fill="none" stroke="#0ea5e9" strokeWidth={1.8} className="w-5 h-5">
                      <path d="M12 5v14M5 12h14" strokeLinecap="round"/>
                    </svg>
                  </div>
                  <p className="text-[13px] font-semibold text-gray-800">{m.serial_number}</p>
                  <p className="text-[12px] text-gray-400 mt-0.5">Schedule recurring top-up</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ── upcoming schedule ── */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-50">
            <h2 className="text-[14px] font-bold text-gray-800">Upcoming Schedule</h2>
            <p className="text-[12px] text-gray-400 mt-0.5">Next 4 scheduled top-ups across all active plans</p>
          </div>
          <div className="divide-y divide-gray-50">
            {activePlans.flatMap((p) => {
              const dates = [];
              const base = new Date(p.next_run);
              for (let i = 0; i < 2; i++) {
                const d = new Date(base);
                if (p.frequency === "weekly") d.setDate(base.getDate() + i * 7);
                else if (p.frequency === "bi_weekly") d.setDate(base.getDate() + i * 14);
                else d.setMonth(base.getMonth() + i);
                dates.push({ plan: p, date: d });
              }
              return dates;
            })
            .sort((a, b) => a.date.getTime() - b.date.getTime())
            .slice(0, 6)
            .map((entry, i) => (
              <div key={i} className="flex items-center gap-4 px-5 py-3.5">
                <div className="w-9 h-9 rounded-xl bg-sky-50 flex items-center justify-center flex-shrink-0">
                  <svg viewBox="0 0 24 24" fill="none" stroke="#0ea5e9" strokeWidth={1.8} className="w-4 h-4">
                    <rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18" strokeLinecap="round"/>
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-medium text-gray-800">Meter {entry.plan.meter_serial} — KES {entry.plan.amount.toLocaleString()}</p>
                  <p className="text-[11.5px] text-gray-400">{FREQ_LABELS[entry.plan.frequency]}</p>
                </div>
                <p className="text-[12.5px] font-semibold text-gray-700 flex-shrink-0">
                  {entry.date.toLocaleDateString("en-KE", { dateStyle: "medium" })}
                </p>
              </div>
            ))}
            {activePlans.length === 0 && (
              <div className="px-5 py-8 text-center text-[13px] text-gray-400">No active plans to schedule.</div>
            )}
          </div>
        </div>

        {/* ── billing history ── */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-50">
            <h2 className="text-[14px] font-bold text-gray-800">Billing History</h2>
            <p className="text-[12px] text-gray-400 mt-0.5">Recent recurring payment executions</p>
          </div>
          <div className="divide-y divide-gray-50">
            {[
              { date: "12 May 2026", meter: "MTR-001", freq: "Monthly", amount: 500, status: "success" },
              { date: "12 Apr 2026", meter: "MTR-001", freq: "Monthly", amount: 500, status: "success" },
              { date: "12 Mar 2026", meter: "MTR-001", freq: "Monthly", amount: 500, status: "failed" },
              { date: "12 Feb 2026", meter: "MTR-001", freq: "Monthly", amount: 500, status: "success" },
              { date: "12 Jan 2026", meter: "MTR-001", freq: "Monthly", amount: 500, status: "success" },
            ].map((log, i) => (
              <div key={i} className="flex items-center gap-4 px-5 py-3.5">
                <div className={`w-2 h-2 rounded-full flex-shrink-0 ${log.status === "success" ? "bg-emerald-400" : "bg-red-400"}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-medium text-gray-800">Meter {log.meter} — KES {log.amount.toLocaleString()}</p>
                  <p className="text-[11.5px] text-gray-400">{log.freq} · {log.date}</p>
                </div>
                <span className={`text-[11.5px] font-semibold px-2.5 py-0.5 rounded-full ${log.status === "success" ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-500"}`}>
                  {log.status === "success" ? "Completed" : "Failed"}
                </span>
              </div>
            ))}
          </div>
        </div>

      </div>

      {modalOpen && (
        <PlanModal
          meter={modalMeter}
          existing={editPlan}
          onClose={() => setModalOpen(false)}
          onSave={handleSave}
        />
      )}
    </>
  );
}
